import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  Flex,
  Box,
  useToast,
  Text,
} from '@chakra-ui/react';
import { baseToastProps } from '../constants';
import { tokenizedRequest } from '../utils/customAxios';
import { FiSend } from 'react-icons/fi';

const SendInvoiceModal = ({ isOpen, onClose, selectedInvoiceRow }) => {
  const [emails, setEmails] = useState(['']);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const addEmailField = () => {
    if (emails.length < 5) setEmails([...emails, '']);
  };

  const removeEmailField = index => {
    const newEmails = [...emails];
    newEmails.splice(index, 1);
    setEmails(newEmails);
  };

  const validateEmail = email =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSend = async () => {
    // Trim emails and check for empty or invalid values
    const trimmedEmails = emails.map(e => e.trim());
    const emptyEmail = trimmedEmails.find(email => email === '');
    if (emptyEmail !== undefined) {
      toast({
        ...baseToastProps,
        status: 'error',
        description: 'Please fill in all email fields before sending.',
      });
      return;
    }
  
    const invalidEmail = trimmedEmails.find(email => !validateEmail(email));
    if (invalidEmail) {
      toast({
        ...baseToastProps,
        status: 'error',
        description: `Invalid email: ${invalidEmail}`,
      });
      return;
    }
  
    const reqObj = {
      method: 'POST',
      url: `${process.env.REACT_APP_API_URL}/billing/invoices/receipt`,
      data: {
        emails: trimmedEmails,
        invoiceIds: [selectedInvoiceRow.invoiceId],
      },
    };
  
    setIsLoading(true);
  
    try {
      const response = await tokenizedRequest(reqObj);
  
      if (!response?._success) {
        throw new Error('Send failed');
      }
  
      toast({
        ...baseToastProps,
        status: 'success',
        description: 'Receipts sent successfully.',
      });
  
      onClose();
      setEmails(['']);
    } catch (error) {
      toast({
        ...baseToastProps,
        status: 'error',
        description: error.message || 'Failed to send receipts.',
      });
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      closeOnOverlayClick={false}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Send Invoice Receipts</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={4} p={3} bg="gray.50" borderRadius="md">
            {selectedInvoiceRow.dateTime && (
              <Text fontSize="sm" color="gray.600" mb={1}>
                <strong>Date:</strong>{' '}
                {new Date(selectedInvoiceRow.dateTime).toLocaleString()}
              </Text>
            )}
            {selectedInvoiceRow.description && (
              <>
                <Text fontWeight="medium">Invoice Description:</Text>
                <Text>{selectedInvoiceRow.description}</Text>
              </>
            )}
          </Box>
          {emails.map((email, index) => (
            <Flex key={index} mb={2}>
              <Input
                value={email}
                onChange={e => handleEmailChange(index, e.target.value)}
                placeholder={`Email ${index + 1}`}
                isInvalid={email && !validateEmail(email)}
              />
              {emails.length > 1 && (
                <Button
                  ml={2}
                  onClick={() => removeEmailField(index)}
                  colorScheme="red"
                  size="sm"
                >
                  Remove
                </Button>
              )}
            </Flex>
          ))}
          {emails.length < 5 && (
            <Button
              variant="link"
              onClick={addEmailField}
              mt={2}
              _hover={{ backgroundColor: 'white' }}
            >
              + Add Another Email
            </Button>
          )}
        </ModalBody>
        <ModalFooter justifyContent="space-between">
          <Button variant="ghost" onClick={onClose} isLoading={isLoading}>
            Close
          </Button>
          <Button
            onClick={handleSend}
            isLoading={isLoading}
            colorScheme="teal"
            leftIcon={<FiSend />}
          >
            Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SendInvoiceModal;
